class VideoPlayer extends HTMLElement {
  video = null;

  connectedCallback() {
    const video = this.querySelector('video');
    if (video) {
      this.video = video;
      this.initialize();
    }
  }

  initialize() {
    // Select elements in the dom that we need for the video player
    this.playPause = this.querySelector('[data-video="play-pause"]');
    this.muteButtons = this.querySelectorAll('[data-video-mute]');

    // Ensure autoplay videos are muted
    if (this.video.autoplay) {
      this.video.muted = true;
      this.classList.add('muted');
    }

    // Add event listeners to elements
    if (this.playPause) {
      this.playPause.addEventListener('click', () => {
        this.togglePlayPause();
      });
    }

    this.video.addEventListener('click', () => {
      this.togglePlayPause();
    });

    this.muteButtons?.forEach((muteButton) => {
      muteButton?.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleMute();
      });
    });

    this.video.addEventListener('ended', () => {
      console.log('ended');
      if (!this.video.loop) {
        this.classList.remove('playing');
      }
    });
  }

  /**
   * togglePlayPause
   * @description Set the video to play or pause.
   */
  async togglePlayPause() {
    if (this.video.paused) {
      try {
        await this.playVideo();
        this.playPause?.setAttribute('aria-pressed', 'true');
      } catch {
        // Playback failed (e.g. browser policy).
      }
    } else {
      this.pauseVideo();
      this.playPause?.setAttribute('aria-pressed', 'false');
    }
  }

  /**
   * toggleMute
   * @description Toggle between muted and unmuted state.
   */
  toggleMute() {
    if (this.video.muted) {
      this.unmuteVideo();
      this.muteButtons.forEach((muteButton) => {
        muteButton.setAttribute('aria-pressed', 'false');
      });
    } else {
      this.muteVideo();
      this.muteButtons.forEach((muteButton) => {
        muteButton.setAttribute('aria-pressed', 'true');
      });
    }
  }

  /**
   * unmuteVideo
   */
  unmuteVideo() {
    this.video.muted = false;
    this.video.removeAttribute('muted');
    this.classList.remove('muted');
  }

  /**
   * muteVideo
   */
  muteVideo() {
    this.video.muted = true;
    this.video.setAttribute('muted', '');
    this.classList.add('muted');
  }

  /**
   * pauseVideo
   */
  pauseVideo() {
    this.video.pause();
    this.classList.remove('playing');
  }

  /**
   * loadSources
   * @description Inject deferred video sources on first play.
   */
  loadSources() {
    if (!this.video.hasAttribute('data-defer-src')) {
      return Promise.resolve();
    }

    const template = this.querySelector('template[data-video-sources]');
    if (template) {
      this.video.append(...template.content.cloneNode(true).childNodes);
      template.remove();
    }

    this.video.removeAttribute('data-defer-src');
    this.video.load();

    return new Promise((resolve) => {
      if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        resolve();
        return;
      }

      this.video.addEventListener('loadeddata', () => resolve(), { once: true });
    });
  }

  /**
   * playVideo
   */
  async playVideo() {
    await this.loadSources();
    await this.video.play();
    this.classList.add('playing');
  }
}

customElements.define('video-player', VideoPlayer);
